import { Splide } from '@hh/global.js'

class SrOnlyArrowSlider extends HTMLElement {
  constructor() {
    super()

    this.initialSpeed = 400
    this.maxSlideTranslateX = 0

    this.thumbWidth = 0
    this.isMovingScrollBar = false
    this.splideListObserver = null
    this.splideListObserverConfig = {
      attributes: true,
      attributeFilter: ['style'],
    }

    this._boundHandleResize = this._handleResize.bind(this)
    this._boundHandleDown = this._handleDown.bind(this)
    this._boundHandleUp = this._handleUp.bind(this)
    this._boundHandleMove = this._handleMove.bind(this)
  }

  connectedCallback() {
    this.hasScrollBar = this.hasAttribute('has-scroll-bar')
    this.scrollBarStyle = this.getAttribute('has-scroll-bar') || 'scrollbar'
    this.scrollBar = this.querySelector('[data-scroll-bar]')
    this.splideList = this.querySelector('[data-splide-list]')

    this.initSlider()
    this.adjustButtons()
    this.initScrollbar()
  }

  disconnectedCallback() {
    if (this.slider) {
      this.slider.destroy(true)
      this.slider = null
    }
    this.splideListObserver?.disconnect()
    window.removeEventListener('resize', this._boundHandleResize)
    window.removeEventListener('pointerup', this._boundHandleUp)
    window.removeEventListener('pointermove', this._boundHandleMove)
    this.scrollBar?.removeEventListener('pointerdown', this._boundHandleDown)
  }

  initSlider() {
    if (this.slider) {
      return
    }

    const autoWidth = this.hasAttribute('auto-width')

    const options = {
      drag: 'free',
      arrows: true,
      pagination: false,
      autoWidth: autoWidth,
      speed: this.initialSpeed,
    }

    this.slider = new Splide(this, options).mount()
  }

  adjustButtons() {
    const prevButton = this.querySelector('[data-prev-button]')
    const nextButton = this.querySelector('[data-next-button]')
    prevButton?.setAttribute(
      'aria-label',
      `${this.getAttribute('slider-title') ?? ''}, Previous Slide`
    )
    nextButton?.setAttribute(
      'aria-label',
      `${this.getAttribute('slider-title') ?? ''}, Next Slide`
    )
    this.prepend(prevButton ?? '')
    this.append(nextButton ?? '')
  }

  initScrollbar() {
    if (!this.hasScrollBar || !this.scrollBar || !this.splideList) {
      return
    }

    this.getScrollLimits()
    this.updateScrollBarProgress()
    this.initObserver()

    window.addEventListener('resize', this._boundHandleResize)
    window.addEventListener('pointerup', this._boundHandleUp)
    window.addEventListener('pointermove', this._boundHandleMove)
    this.scrollBar.addEventListener('pointerdown', this._boundHandleDown)
  }

  initObserver() {
    this.splideListObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'style'
        ) {
          this.updateScrollBarProgress()
        }
      })
    })

    this.splideListObserver.observe(
      this.splideList,
      this.splideListObserverConfig
    )
  }

  getScrollLimits() {
    if (!this.scrollBar) return

    const numberOfSlides =
      this.slider.Components?.Elements?.slides?.length ?? 10

    this.slider.options.speed = 0
    this.slider.go(numberOfSlides)
    this.maxSlideTranslateX = this.getTranslateXFromElement(this.splideList)

    this.slider.go(0)
    this.slider.options.speed = this.initialSpeed

    const scrollBarWidth = this.scrollBar.offsetWidth
    this.thumbWidth =
      this.scrollBarStyle === 'slide-index'
        ? scrollBarWidth / numberOfSlides
        : scrollBarWidth ** 2 / (scrollBarWidth - this.maxSlideTranslateX)

    const thumbWidthPercent = (this.thumbWidth / scrollBarWidth) * 100
    this.style.setProperty('--scroll-thumb-width', `${thumbWidthPercent}`)

    this.scrollBar.classList.toggle(
      'hidden',
      Math.abs(this.maxSlideTranslateX) <= 0
    )
  }

  updateScrollBarProgress() {
    const currentTranslateX = this.getTranslateXFromElement(this.splideList)
    const percentScrolled = (currentTranslateX / this.maxSlideTranslateX) * 100
    const cappedPercentScrolled = Math.max(0, Math.min(100, percentScrolled))

    this.style.setProperty('--scroll-percent', `${cappedPercentScrolled}`)
  }

  getTranslateXFromElement(elm) {
    if (!elm) {
      return 0
    }

    const style = window.getComputedStyle(elm)
    const transform = style.transform || 'none'

    if (transform === 'none') {
      return 0
    }
    const matrix = new DOMMatrix(transform)
    return matrix.m41
  }

  _handleResize() {
    this.getScrollLimits()
    this.updateScrollBarProgress()
  }

  _handleDown(event) {
    event?.preventDefault()

    this.isMovingScrollBar = true
    this.splideListObserver?.disconnect()
  }

  _handleUp(event) {
    event?.preventDefault()

    requestAnimationFrame(() => {
      this.isMovingScrollBar = false
      this.splideListObserver?.observe(
        this.splideList,
        this.splideListObserverConfig
      )
    })
  }

  _handleMove(event) {
    if (!this.isMovingScrollBar || !this.splideList) {
      return
    }

    event?.preventDefault()

    const scrollBarRect = this.scrollBar.getBoundingClientRect()
    const pointerX = event.clientX - scrollBarRect.left

    const scrollBarWidth = this.scrollBar.offsetWidth
    const maxThumbX = scrollBarWidth - this.thumbWidth
    if (maxThumbX <= 0) {
      return
    }

    const cappedPointerX = Math.max(
      0,
      Math.min(maxThumbX, pointerX - this.thumbWidth / 2)
    )
    const percentScrolled = cappedPointerX / maxThumbX

    const targetTranslateX = this.maxSlideTranslateX * percentScrolled
    this.splideList.style.transform = `translateX(${targetTranslateX}px)`

    this.scrollBar.setAttribute(
      'aria-valuenow',
      Math.round(percentScrolled * 100)
    )

    this.updateScrollBarProgress()
  }
}

customElements.define('free-slider', SrOnlyArrowSlider)
